// src/contexts/OfflineSyncContext.tsx
// Contexte global pour suivre les opérations en cours de synchronisation hors ligne / arrière-plan.

import React, { createContext, useContext, useState, useCallback } from 'react';

interface OfflineSyncState {
  pendingCount: number;
  lastSuccessMessage: string | null;
  lastErrorMessage: string | null;
  registerPending: () => void;
  registerSuccess: (message?: string) => void;
  registerError: (message?: string) => void;
}

const defaultState: OfflineSyncState = {
  pendingCount: 0,
  lastSuccessMessage: null,
  lastErrorMessage: null,
  registerPending: () => {},
  registerSuccess: () => {},
  registerError: () => {},
};

const OfflineSyncContext = createContext<OfflineSyncState>(defaultState);

export const OfflineSyncProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [pendingCount, setPendingCount] = useState(0);
  const [lastSuccessMessage, setLastSuccessMessage] = useState<string | null>(null);
  const [lastErrorMessage, setLastErrorMessage] = useState<string | null>(null);

  const registerPending = useCallback(() => {
    setPendingCount((prev) => prev + 1);
  }, []);

  const registerSuccess = useCallback((message?: string) => {
    setPendingCount((prev) => Math.max(prev - 1, 0));
    if (message) {
      setLastSuccessMessage(message);
      setLastErrorMessage(null);
    }
  }, []);

  const registerError = useCallback((message?: string) => {
    setPendingCount((prev) => Math.max(prev - 1, 0));
    if (message) {
      setLastErrorMessage(message);
      setLastSuccessMessage(null);
    }
  }, []);

  return (
    <OfflineSyncContext.Provider
      value={{
        pendingCount,
        lastSuccessMessage,
        lastErrorMessage,
        registerPending,
        registerSuccess,
        registerError,
      }}
    >
      {children}
    </OfflineSyncContext.Provider>
  );
};

export const useOfflineSync = (): OfflineSyncState => {
  const ctx = useContext(OfflineSyncContext);
  return ctx;
};

